<template>
    <div class="edit-segment-overlay">
        <pendo-drawer
            v-if="!isAdoptSegment"
            :visible="visible"
            :title="title"
            :class-name="drawerClass"
            size="fullscreen"
            :z-index="zIndex"
            @open="onDrawerOpen"
            @opened="onDrawerOpened"
            @before-close="onDrawerBeforeClose"
            @closed="resetLocalState">
            <template #body>
                <adopt-segment-overlay v-if="isAdoptSegment" />
                <template v-if="!isAdoptSegment">
                    <div
                        v-if="showSettingsPanel"
                        class="settings-panel">
                        <div class="settings-panel__inner">
                            <pendo-card
                                v-if="verificationState === 'requested' && canVerify"
                                class="verification-card"
                                collapsible
                                body-min-height="0"
                                footer-min-height="26px"
                                :title="$t('editSegmentOverlay.card.Verification request')">
                                <i18n-t
                                    keypath="editSegmentOverlay.requestMetadata.mainMessage"
                                    tag="p"
                                    class="request-metadata">
                                    <template #verificationUser>
                                        <strong>{{ verificationLastUpdatedByUser }}</strong>
                                    </template>
                                    <template #verificationDisplay>
                                        {{ verificationLastUpdatedAtDisplay }}
                                    </template>
                                </i18n-t>
                                <pendo-input
                                    v-if="verificationNotes"
                                    :value="verificationNotes"
                                    type="textarea"
                                    :rows="3"
                                    disabled />
                                <template #footer>
                                    <pendo-button
                                        theme="app"
                                        size="mini"
                                        type="danger"
                                        :label="$t('editSegmentOverlay.footer.Reject')"
                                        :disabled="isSaving || loadingVerification"
                                        @click="onRejectVerification" />
                                    <pendo-button
                                        theme="app"
                                        size="mini"
                                        type="primary"
                                        :label="
                                            hasUnacknowledgedChanges
                                                ? $t('editSegmentOverlay.footer.Save and accept')
                                                : $t('editSegmentOverlay.footer.Accept')
                                        "
                                        :disabled="verificationAcceptButtonDisabled"
                                        @click="onAcceptVerification" />
                                </template>
                            </pendo-card>
                            <pendo-card
                                class="settings-card"
                                body-min-height="0"
                                :title="$t('editSegmentOverlay.footer.Settings')">
                                <template v-if="showAnonymousOptions">
                                    <div class="settings-panel__section">
                                        <anon-visitor-radio-options
                                            :value="advancedOptions.selectedVisitorType"
                                            :disable-anonymous-option="disableAnonymousOption"
                                            @input="onAnonVisitorInput" />
                                    </div>
                                </template>
                                <template v-if="!isCustomSegment">
                                    <pendo-form
                                        v-model="isFormValid"
                                        disable-auto-focus-on-validation-error
                                        :model="localSegment">
                                        <div class="settings-panel__section">
                                            <div class="name-input">
                                                <pendo-form-item
                                                    :rules="[
                                                        {
                                                            required: true,
                                                            message: $t('editSegmentOverlay.form.Name is required'),
                                                            trigger: 'change',
                                                        },
                                                    ]"
                                                    prop="name"
                                                    :label="$t('editSegmentOverlay.form.Name')">
                                                    <pendo-input
                                                        :aria-label="$t('editSegmentOverlay.form.Name')"
                                                        :value="localSegment.name"
                                                        @input="updateName" />
                                                </pendo-form-item>
                                            </div>
                                            <div
                                                v-if="createdBySomeoneElse"
                                                class="created-by">
                                                <div class="created-by-label">
                                                    {{ $t('editSegmentOverlay.form.Created by') }}
                                                </div>
                                                <div class="created-by-details">
                                                    <pendo-icon
                                                        type="alert-circle-solid"
                                                        size="18"
                                                        fill="var(--palette-yellow-70)"
                                                        stroke="var(--palette-gray-0)" />
                                                    <span>{{ localSegment.createdByUser.username }}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <pendo-divider aria-hidden="true" />
                                        <div class="settings-panel__section">
                                            <div class="description-input">
                                                <pendo-form-item
                                                    prop="description"
                                                    :label="$t('editSegmentOverlay.form.Description')">
                                                    <pendo-input
                                                        ref="descriptionTextarea"
                                                        v-model="localSegment.description"
                                                        type="textarea"
                                                        rows="1"
                                                        :aria-label="$t('editSegmentOverlay.form.Add a description for this segment')"
                                                        :placeholder="$t('editSegmentOverlay.form.Add a description')"
                                                        :invalid="hasDescriptionError"
                                                        @input="onDescriptionInput">
                                                        <template #bottomLabel>
                                                            <span
                                                                v-if="descriptionCharacterCount >= 200"
                                                                class="description-input__character-count"
                                                                :class="{ 'description-input__character-count--error': hasDescriptionError }">
                                                                {{ descriptionCharacterCount }} / 280
                                                            </span>
                                                        </template>
                                                    </pendo-input>
                                                </pendo-form-item>
                                            </div>
                                        </div>
                                        <pendo-divider aria-hidden="true" />
                                        <div class="settings-panel__section">
                                            <div class="visibility-options">
                                                <pendo-radio-group
                                                    v-pendo-tooltip="{
                                                        arrow: false,
                                                        multiline: true,
                                                        content: insightsVisibilityTooltip,
                                                        placement: 'right',
                                                    }"
                                                    :value="localSegment.shared"
                                                    :label="$t('editSegmentOverlay.form.Visibility')"
                                                    :disabled="isInsightsSegment"
                                                    button
                                                    @change="updateVisibility">
                                                    <pendo-radio-button
                                                        :selected="localSegment.shared"
                                                        :disabled="visibilityOptions[0].disabled"
                                                        :value="true"
                                                        :label="$t('editSegmentOverlay.form.Everyone')" />
                                                    <pendo-radio-button
                                                        v-pendo-tooltip="{
                                                            arrow: true,
                                                            multiline: true,
                                                            content: visibilityTooltip,
                                                            delay: 500,
                                                        }"
                                                        :selected="!localSegment.shared"
                                                        :disabled="visibilityOptions[1].disabled"
                                                        :value="false"
                                                        :label="$t('editSegmentOverlay.form.Only me')" />
                                                </pendo-radio-group>
                                            </div>
                                        </div>
                                        <template v-if="showVerificationSection">
                                            <pendo-divider aria-hidden="true" />
                                            <div class="settings-panel__section">
                                                <div class="settings-panel__label">
                                                    {{ $t('editSegmentOverlay.form.Verification') }}
                                                </div>
                                                <verification-status
                                                    :value="verificationState"
                                                    :can-verify="canVerify"
                                                    :note="verificationNotes"
                                                    :allow-note="!isApiGeneratedSegment"
                                                    :updated-by="verificationLastUpdatedByUser"
                                                    :updated-at="verificationLastUpdatedAt"
                                                    :entity-name="verificationEntityName"
                                                    entity-type="segment"
                                                    :read-only="readOnlyVerification"
                                                    :loading="loadingVerification"
                                                    @input="handleVerificationStatusChange" />
                                            </div>
                                        </template>
                                    </pendo-form>
                                </template>
                                <template v-if="!isInsightsSegment && !isCustomSegment && showAnonymousEnabled">
                                    <pendo-divider aria-hidden="true" />
                                    <div class="settings-panel__section">
                                        <anon-visitor-radio-options
                                            :value="advancedOptions.selectedVisitorType"
                                            :disable-anonymous-option="disableAnonymousOption"
                                            @input="onAnonVisitorInput" />
                                    </div>
                                </template>
                                <template v-if="segmentsUsedCount && !isInsightsSegment">
                                    <pendo-divider aria-hidden="true" />
                                    <div class="segments-used">
                                        <span class="segments-used-label">
                                            {{ $t('editSegmentOverlay.form.Segments Used') }}
                                            <pendo-icon
                                                v-pendo-tooltip="{
                                                    arrow: true,
                                                    multiline: true,
                                                    content: $t(
                                                        'editSegmentOverlay.The number of segments you can use is limited to {ruleLimit}.',
                                                        { ruleLimit: SEGMENT_RULE_LIMIT },
                                                    ),
                                                }"
                                                type="info"
                                                size="14" />
                                        </span>
                                        <span class="segments-used-count">
                                            {{
                                                $t('editSegmentOverlay.{segmentsUsedCount} of {ruleLimit}', {
                                                    segmentsUsedCount,
                                                    ruleLimit: SEGMENT_RULE_LIMIT,
                                                })
                                            }}
                                        </span>
                                    </div>
                                </template>
                                <segment-stats
                                    v-if="isInsightsSegment"
                                    :segment="localSegment"
                                    :insights-segment-app-id="insightsSelectedApp.id || ''"
                                    :show-eligible-visitor-count="insightsSegmentShowEligibleVisitorsCount"
                                    :show-insights-segment-eligibility="isInsightsSegment"
                                    :is-valid="isValid"
                                    :segment-rule-count="segmentsUsedCount"
                                    :show-guide-eligibility="!isCustomSegment"
                                    @eligible-visitors-updated="onEligibleVisitorsUpdated" />
                                <template v-if="!isCustomSegment && showSegmentFlag">
                                    <pendo-divider aria-hidden="true" />
                                    <div class="settings-panel__section">
                                        <div class="flag-input">
                                            <pendo-input
                                                :labels="{ top: $t('editSegmentOverlay.form.Segment Flag') }"
                                                :value="localSegment.flagName"
                                                :disabled="createdBySomeoneElse"
                                                :invalid="!segmentFlagNameIsValid"
                                                @input="updateSegmentFlag">
                                                <template #topLabel>
                                                    <div class="segment-flag-label">
                                                        <i18n-t
                                                            keypath="editSegmentOverlay.form.segmentFlag.mainMessage"
                                                            tag="span">
                                                            <template #superOnly>
                                                                <span class="super-only">{{
                                                                    $t('editSegmentOverlay.form.superOnly')
                                                                }}</span>
                                                            </template>
                                                        </i18n-t>
                                                        <pendo-icon
                                                            v-pendo-tooltip="{
                                                                arrow: true,
                                                                multiline: true,
                                                                content: $t(
                                                                    'editSegmentOverlay.Add a segment flag name to use this segment to hide or show features in code.',
                                                                ),
                                                            }"
                                                            type="info"
                                                            size="14" />
                                                        <pendo-icon
                                                            v-if="!isGuideEligible"
                                                            v-pendo-tooltip="{
                                                                arrow: true,
                                                                multiline: true,
                                                                content: $t(
                                                                    'editSegmentOverlay.Segment flag segments must be guide eligible.',
                                                                ),
                                                            }"
                                                            type="alert-triangle"
                                                            size="14" />
                                                    </div>
                                                </template>
                                            </pendo-input>
                                        </div>
                                    </div>
                                </template>
                                <template
                                    v-if="
                                        !isCustomSegment &&
                                            localSegment.flagName &&
                                            localSegment.createdByUser &&
                                            createdBySomeoneElse
                                    ">
                                    <pendo-divider aria-hidden="true" />
                                    <div class="settings-panel__section">
                                        <pendo-alert type="warning">
                                            <i18n-t
                                                keypath="editSegmentOverlay.settingsWarning"
                                                tag="span">
                                                <template #creator>
                                                    <b>{{
                                                        localSegment?.createdByUser?.username ||
                                                            $t('editSegmentOverlay.the segment creator')
                                                    }}</b>
                                                </template>
                                            </i18n-t>
                                        </pendo-alert>
                                    </div>
                                </template>
                                <template v-if="config && isSuperUser">
                                    <pendo-divider aria-hidden="true" />
                                    <div class="settings-panel__section">
                                        <div class="conditions-label">
                                            <i18n-t
                                                keypath="editSegmentOverlay.form.conditions.mainMessage"
                                                tag="span">
                                                <template #superOnly>
                                                    <span class="super-only">{{
                                                        $t('editSegmentOverlay.form.superOnly')
                                                    }}</span>
                                                </template>
                                            </i18n-t>
                                        </div>
                                        <div class="conditions-pipeline">
                                            <pendo-button
                                                v-pendo-tooltip="{
                                                    content: $t('editSegmentOverlay.Open in Agg Builder'),
                                                    arrow: true,
                                                }"
                                                theme="app"
                                                type="tertiary"
                                                size="mini"
                                                @click="onOpenAggBuilder">
                                                <img
                                                    height="14"
                                                    width="14"
                                                    src="@img/icons/pendo-logo-icon.svg">
                                            </pendo-button>
                                            <pendo-button
                                                v-pendo-tooltip="{
                                                    content: $t('editSegmentOverlay.Copy to clipboard'),
                                                    arrow: true,
                                                }"
                                                v-pendo-clipboard="pipelineJson"
                                                theme="app"
                                                type="tertiary"
                                                size="mini"
                                                icon="clipboard"
                                                icon-size="14" />
                                            <pre><code>{{ pipelineJson }}</code></pre>
                                        </div>
                                    </div>
                                </template>
                            </pendo-card>
                        </div>
                    </div>
                    <template v-if="isDrawerOpened">
                        <div class="rules-panel">
                            <div
                                v-if="isInsightsSegment"
                                class="app-chooser-container">
                                <span>
                                    {{ $t('editSegmentOverlay.insightsDisclaimer') }}
                                </span>
                                <single-app-chooser
                                    :app-options="appChooserOptions"
                                    :is-loading="isLoadingApps"
                                    :selected-app="insightsSelectedApp"
                                    @set-application="insightsSelectedApp = $event" />
                            </div>
                            <div
                                v-if="isSmartSegment"
                                class="create-smart-segment-disclaimer">
                                {{ $t('editSegmentOverlay.smartSegmentDisclaimer') }}
                            </div>
                            <segment-editor
                                v-if="showRulesPanel"
                                :can-edit-and-share="canShare"
                                :check-valid-rule="checkValidRule"
                                :config="config"
                                :read-only="isSegmentEditorReadOnly"
                                :segment="localSegment"
                                :has-dependent-segment="hasDependentSegment"
                                @update="onSegmentEditorUpdate" />
                            <pendo-loading-indicator
                                v-if="!ready"
                                size="medium" />
                        </div>
                        <div
                            v-if="!isInsightsSegment"
                            class="metrics-panel">
                            <segment-metrics-panel
                                :segment="localSegment"
                                :show-eligible-visitor-count="!isInsightsSegment"
                                :is-valid="isValid"
                                :show-guide-eligibility="!isCustomSegment"
                                :show-dependencies-tab="isBuiltSegment"
                                @eligible-visitors-updated="onEligibleVisitorsUpdated" />
                        </div>
                        <pendo-modal
                            :visible="showConfirmExitModal"
                            type="confirmation"
                            :title="$t('editSegmentOverlay.Unsaved changes')"
                            :message="
                                $t(
                                    'If you exit this page without saving, all changes will be lost. Are you sure you want to exit?',
                                )
                            "
                            :cancel-button-config="{ label: $t('editSegmentOverlay.Continue editing') }"
                            :confirm-button-config="{ label: $t('Exit') }"
                            @cancel="onDismissExitModal"
                            @confirm="onConfirmExitModal" />
                        <delete-segment-modal
                            v-if="showDeleteSegmentModal"
                            :is-delete-modal-open="showDeleteSegmentModal"
                            :segments-to-delete="segmentsToDelete"
                            :segment-dependents="segmentDependents"
                            @delete="onDeleteSegment"
                            @cancel="closeDeleteSegmentModal" />
                        <pendo-modal
                            class="delete-segment-modal"
                            :visible="showChangeVisibilityModal"
                            type="confirmation"
                            :title="$t('Save segment')"
                            @close="onDismissChangeVisibilityModal">
                            <template #body>
                                <i18n-t
                                    keypath="editSegmentOverlay.visiblityModal.mainMessage"
                                    tag="span">
                                    <template #visibilityToEveryone>
                                        <strong>{{
                                            $t('editSegmentOverlay.visiblityModal.visibility to Everyone')
                                        }}</strong>
                                    </template>
                                </i18n-t>
                            </template>
                            <template #footer>
                                <div class="delete-segment-modal__footer">
                                    <pendo-button
                                        theme="app"
                                        type="secondary"
                                        :label="$t('Cancel')"
                                        class="cancel-delete-segment"
                                        @click="onDismissChangeVisibilityModal" />
                                    <pendo-button
                                        theme="app"
                                        type="primary"
                                        :label="$t('Save and Change Visibility')"
                                        class="save-segment"
                                        @click="onSaveChangeVisibilityModal" />
                                </div>
                            </template>
                        </pendo-modal>
                        <pendo-modal
                            class="replay-confirm-save-modal"
                            data-cy="replay-confirm-save-modal"
                            :visible="showReplayConfirmSaveModal"
                            type="confirmation"
                            title="Update segment?"
                            @close="showReplayConfirmSaveModal = false">
                            <template #body>
                                {{ $t('editSegmentOverlay.replayConfirmSaveModal.body') }}
                            </template>
                            <template #footer>
                                <div class="replay-confirm-save-modal__footer">
                                    <pendo-button
                                        theme="app"
                                        type="secondary"
                                        :label="$t('editSegmentOverlay.No, Cancel')"
                                        class="cancel-save-segment"
                                        @click="showReplayConfirmSaveModal = false" />
                                    <pendo-button
                                        theme="app"
                                        type="danger"
                                        :loading="isSaving"
                                        :label="$t('editSegmentOverlay.Yes, Update')"
                                        class="save-segment"
                                        @click="onSaveReplayConfirmModal" />
                                </div>
                            </template>
                        </pendo-modal>
                        <verification-modal
                            v-if="showConfirmRequestActionModal"
                            :visible="showConfirmRequestActionModal"
                            :action="confirmRequestAction"
                            :updated-by="verificationLastUpdatedByUser"
                            :updated-at="verificationLastUpdatedAt"
                            :entity-name="verificationEntityName"
                            entity-type="segment"
                            @confirm="onConfirmRequestAction"
                            @cancel="onDismissRequestAction" />
                        <pendo-modal
                            type="confirmation"
                            :visible="showConfirmResetRequestModal"
                            show-close
                            :title="$t('editSegmentOverlay.Verification changes')"
                            :confirm-button-config="{ label: $t('editSegmentOverlay.Continue') }"
                            @confirm="onConfirmResetRequest"
                            @cancel="showConfirmResetRequestModal = false"
                            @close="showConfirmResetRequestModal = false">
                            <template #body>
                                <template v-if="localSegment.shared">
                                    <p>
                                        {{
                                            $t(
                                                'editSegmentOverlay.Making changes to a segment will change the verification to unverified.',
                                            )
                                        }}
                                    </p>
                                    <pendo-checkbox
                                        v-if="canVerify && verificationState === 'verified'"
                                        v-model="keepVerifiedStatus">
                                        {{ $t('editSegmentOverlay.Keep segment as verified') }}
                                    </pendo-checkbox>
                                </template>
                                <template v-else>
                                    <i18n-t
                                        keypath="editSegmentOverlay.verificationModal.mainMessage"
                                        tag="p">
                                        <template #onlyMe>
                                            <strong>{{ $t('editSegmentOverlay.Only me') }}</strong>
                                        </template>
                                    </i18n-t>
                                </template>
                            </template>
                        </pendo-modal>
                    </template>
                </template>
            </template>
            <template #footer>
                <footer class="segment-builder__footer">
                    <span
                        v-pendo-tooltip="{
                            arrow: true,
                            multiline: true,
                            content: deleteTooltip,
                        }">
                        <pendo-button
                            v-if="showDeleteButton"
                            class="delete-segment"
                            theme="app"
                            type="danger"
                            :label="$t('editSegmentOverlay.Delete segment')"
                            :disabled="deleteDisabled"
                            @click="openDeleteSegmentModal" />
                    </span>
                    <pendo-button
                        class="cancel-segment"
                        theme="app"
                        type="secondary"
                        :label="cancelButtonText"
                        @click="onDrawerCancel" />
                    <pendo-button
                        v-if="showSaveButton"
                        v-pendo-tooltip="{
                            arrow: true,
                            multiline: true,
                            content: saveButtonTooltip,
                        }"
                        :label="saveButtonText"
                        class="save-segment"
                        theme="app"
                        type="primary"
                        :loading="isSaving"
                        :disabled="saveButtonDisabled"
                        @click="onSave" />
                </footer>
            </template>
        </pendo-drawer>
    </div>
    <div v-if="isAdoptSegment">
        <adopt-segment-builder
            :config="{
                userId: user.id,
                data: {
                    appFirstVisit,
                    apps: listAllForActiveSubscription,
                    schemas: filteredSchemaList,
                    guides: { map: guideMap, filteredList: () => guideList },
                    rcModules: { map: rcModulesMap, filteredList: () => rcModulesList },
                    draftResourceCenters: { map: draftResourceCenterMap, filteredList: () => draftResourceCenterList },
                    pages: { map: pageMap, filteredList: () => pageList },
                    features: { map: featureMap, filteredList: () => featureList },
                    workflows: { map: workflowMap, filteredList: () => workflowsList },
                    segments: { map: adoptUISegmentsMap, filteredList: () => segmentsListByGroup.customList },
                    trackTypes: { map: trackTypeMap, filteredList: () => trackTypeList }
                },
                filters: { timeSeries: activeTimeSeries },
                hasAdoptCustomPageSegments: hasAdoptCustomPageSegments,
                guideTargeting: editingGuideSegment,
                hasResourceCenter: activeHasResourceCenter,
                hasWorkflow: activeHasWorkflows,
                workflowWithinLastDateRange: workflowWithinLastDateRange,
                showWorkflowRicherSegmentation: true,
                showAppicons: true,
                canShowAnonymous: canShowAnonymousVisitors,
                useTrackTypes: true
            }"
            :editing-segment="editor.segment"
            :is-saving="isSaving"
            :is-visible="visible"
            :show-super-panel="isSuper"
            @save="onLegacyAdoptSegmentSave"
            @close="onDrawerCancel"
            @before-close="onDrawerBeforeClose"
            @closed="resetLocalState"
            @create-new-segment="onCreateNewSegment" />
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters, mapMutations } from 'vuex';
import {
    PendoAlert,
    PendoButton,
    PendoCard,
    PendoCheckbox,
    PendoClipboard,
    PendoDivider,
    PendoDrawer,
    PendoForm,
    PendoFormItem,
    PendoIcon,
    PendoInput,
    PendoLoadingIndicator,
    PendoModal,
    PendoNotification,
    PendoRadioGroup,
    PendoRadioButton,
    PendoTooltip,
} from '@pendo/components';

import DeleteSegmentModal from '@/segments-page/modals/delete-segment-modal.vue';
import SegmentEditor from '@/segments/components/segment-editor-vue/segment-editor.vue';
import SegmentStats from '@/segments/components/segment-editor-vue/components/segment-stats.vue';
import SegmentMetricsPanel from '@/segments/components/segment-editor-vue/components/segment-metrics-panel.vue';
import AnonVisitorRadioOptions from '@/segments/components/segment-editor-vue/anon-visitor-radio-options.vue';
import SegmentEditing from '@/mixins/segment-editing.mixin.js';

import { $canSub } from '@pendo/permissions';
import { removeSpecialChars } from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { isAdoptSegment } from '@/utils/segments.js';
import { isNestedSegment } from '@/vuex/modules/segments/segment.helpers.js';
import AdoptSegmentOverlay from '@/segments/components/segment-editor-vue/adopt-segment-overlay.vue';
import AdoptSegmentBuilder from '@/segments/components/segment-editor-vue/adopt-segment-builder/adopt-segment-builder.vue';
import SingleAppChooser from '@/dashboards-v2/templates/template-settings/common/single-app-chooser.vue';
import get from 'lodash/get';
import omit from 'lodash/omit';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { eligibleVisitorPipeline } from '@/vuex/modules/segments/segment-eligible-visitor-list.aggregation';
import { isExtensionApp } from '@/utils/apps';
import { SEGMENT_RULE_LIMIT } from '@/utils/segments.js';
import { formatUserName, ACTION_STATE_MAP } from '@/common/verification-status/verification-status.utils';
import VerificationStatus from '@/common/verification-status/verification-status.vue';
import VerificationModal from '@/common/verification-status/verification-modal.vue';
import capitalize from 'lodash/capitalize';
import { readOnlyVerification } from '../../../common/verification-status/verification-status.utils';
import moment from '@utils/moment-wrapper';
import { reduceSchema } from './adopt-segment-builder/utils/schema.js';
import { segmentsList, isValidSegmentInContext, isSegmentAttachedToGuide } from './adopt-segment-builder/utils/segments';
import { isEngageSegment } from './adopt-segment-builder/utils/utils';
import keyBy from 'lodash/keyBy';
import min from 'lodash/min';

import { PRODUCT_LINES } from '@/settings/subscription-settings/product-lines';

const DATE_FORMAT = new Intl.DateTimeFormat(undefined, { dateStyle: 'long', timeStyle: 'short' });
const UTM_DATE = 1746057600000;

export default {
    name: 'EditSegmentOverlay',
    components: {
        SingleAppChooser,
        AdoptSegmentOverlay,
        AdoptSegmentBuilder,
        AnonVisitorRadioOptions,
        PendoAlert,
        PendoButton,
        PendoCard,
        PendoCheckbox,
        PendoDivider,
        PendoDrawer,
        PendoForm,
        PendoFormItem,
        PendoIcon,
        PendoInput,
        PendoModal,
        PendoRadioGroup,
        PendoRadioButton,
        PendoLoadingIndicator,
        SegmentEditor,
        SegmentMetricsPanel,
        SegmentStats,
        VerificationModal,
        DeleteSegmentModal,
        VerificationStatus,
    },
    directives: {
        PendoClipboard,
        PendoTooltip,
    },
    mixins: [SegmentEditing],
    provide () {
        return {
            onlyAllowGuideCompatibleRules: () => this.onlyAllowGuideCompatibleRules,
        };
    },
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        guidesOnly: {
            type: Boolean,
            default: false,
        },
        isAskPendoSegment: {
            type: Boolean,
            default: false,
        },
        isReplay: {
            type: Boolean,
            default: false,
        },
        isInsightsSegment: {
            type: Boolean,
            default: false,
        },
        isSmartSegment: {
            type: Boolean,
            default: false,
        },
        targetAppId: {
            type: Number,
            default: null,
        },
        zIndex: {
            type: Number,
            default: 1050,
        },
        location: {
            type: String,
            default: '',
        },
    },
    emits: ['delete', 'save', 'close'],
    data () {
        return {
            SEGMENT_RULE_LIMIT,
            showSettingsPanel: false,
            isDrawerOpened: false,
            ready: false,

            config: null,
            checkValidRule: false,
            // used in segment-editing.mixin.js
            // eslint-disable-next-line internal-rules/no-unused-properties
            isCopy: false,
            showConfirmExitModal: false,
            showDeleteSegmentModal: false,
            showChangeVisibilityModal: false,
            showReplayConfirmSaveModal: false,
            showConfirmRequestActionModal: false,
            confirmRequestAction: '',
            keepVerifiedStatus: false,
            isValid: true,
            isFormValid: true,
            isSaving: false,
            hasUnacknowledgedChanges: false,
            localSegment: {},
            dependentReports: [],
            dependentGuides: [],
            dependentSegments: [],
            dependentCustomSegments: [],
            dependentDraftSegments: [],
            dependentReplayApps: [],
            insightsSelectedApp: {},
            numVisitorsInSegment: {
                count: 0,
                isLoading: false,
            },
            isLoadingApps: false,
            loadingVerification: false,
            showConfirmResetRequestModal: false,
            segmentsToDelete: [],
        };
    },
    computed: {
        ...mapState({
            editor: (state) => state.segments.editor,
            segmentDependents: (state) => state.segments.segmentDependents,
            subscription: (state) => state.subscription.active,
            user: (state) => state.auth.user,
            fetchingSegments: (state) => state.segments.fetching,
            guideMap: (state) => state.guides.map,
            pageMap: (state) => state.pages.map,
            featureMap: (state) => state.features.map,
            workflowMap: (state) => state.workflows.map,
            trackTypeMap: (state) => state.trackTypes.map,
            schemas: (state) => state.metadata.map,
        }),
        ...mapGetters({
            isSuperUser: 'auth/isSuper',
            getApplicationFlag: 'apps/getApplicationFlag',
            parentSegmentsById: 'segments/parentSegmentsById',
            segmentIsGuideFriendly: 'segments/segmentIsGuideFriendly',
            allApps: 'apps/list',
            userId: 'auth/userId',
            minVisitorsInSegment: 'subscription/minVisitorsInSegment',
            getFeatureFlag: 'subscription/getFeatureFlag',
            guideList: 'guides/list',
            workflowsList: 'workflows/list',
            trackTypeList: 'trackTypes/list',
            pageList: 'pages/list',
            featureList: 'features/list',
            listAdoptUISegments: 'segments/listAdoptUISegments',
            rcModulesList: 'guides/resourceCenter/getResourceCenterGuides',
            draftResourceCenterMap: 'guides/resourceCenter/draftResourceCenterMap',
            draftResourceCenterList: 'guides/resourceCenter/draftResourceCenterList',
            activeTimeSeries: 'globalFilters/timeSeries',
            listAllForActiveSubscription: 'apps/listAllForActiveSubscription',
        }),
        descriptionCharacterCount () {
            return this.localSegment.description?.length || 0;
        },
        hasDescriptionError () {
            return this.descriptionCharacterCount > 280;
        },
        verification () {
            return this.editor.segment?.verification || {};
        },
        verificationState () {
            return (this.verification.state || 'Unverified').toLowerCase();
        },
        canVerify () {
            const canVerify = $canSub()
                .entity('segment')
                .action('verification')
                .source('*')
                .target('verified');

            return canVerify;
        },
        showVerificationSection () {
            return !this.isNewSegment && this.localSegment.shared && this.editor.segment?.shared;
        },
        readOnlyVerification () {
            if (!this.canEdit) {
                return true;
            }

            return readOnlyVerification(this.verification, {
                shared: this.editor.segment?.shared,
                userId: this.userId,
                canVerify: this.canVerify,
            });
        },
        verificationNotes () {
            return this.verification.notes;
        },
        verificationLastUpdatedByUser () {
            return formatUserName(this.verification.lastUpdatedByUser || {});
        },
        verificationLastUpdatedAt () {
            return this.verification.lastUpdatedAt;
        },
        verificationLastUpdatedAtDisplay () {
            return DATE_FORMAT.format(this.verificationLastUpdatedAt);
        },
        isApiGeneratedSegment () {
            return this.editor.segment?.createdByApi;
        },
        verificationEntityName () {
            return this.editor.segment?.name;
        },
        appChooserOptions () {
            return this.allApps.filter((app) => !isExtensionApp(app.platform));
        },
        isAdoptSegment () {
            const originalSegment = cloneDeep(this.editor.segment);

            if (!originalSegment?.id || !isAdoptSegment(originalSegment)) return false;

            // checking both not adopt and not engage because engage is checking if the rules in the segment are engage compatible
            return !isEngageSegment(originalSegment);
        },
        canEdit () {
            return (
                this.ownedByCurrentUser ||
                $canSub()
                    .entity('segment')
                    .action('edit')
                    .source('*')
                    .target('shared')
            );
        },
        canDelete () {
            return (
                this.ownedByCurrentUser ||
                $canSub()
                    .entity('segment')
                    .action('remove')
                    .source('*')
                    .target('shared')
            );
        },
        canShare () {
            return (
                $canSub()
                    .entity('segment')
                    .action('share')
                    .source('*')
                    .target('*') && this.canEdit
            );
        },
        showSaveButton () {
            if (this.isAdoptSegment) {
                return false;
            }

            return this.canEdit || this.canShare;
        },
        showDeleteButton () {
            if (this.isAdoptSegment) {
                return false;
            }

            return this.isBuiltSegment && this.canDelete;
        },
        deleteDisabled () {
            if (!this.showRulesPanel) return true;

            return this.existingSegmentHasDependents;
        },
        deleteTooltip () {
            if (!this.deleteDisabled || this.loadingVerification) return null;

            if (this.hasDependentReplayApp) {
                return this.$t(
                    'editSegmentOverlay.This segment cannot be deleted because it is currently referenced within guides, replay apps, and/or other segments.',
                );
            }

            return this.$t(
                'editSegmentOverlay.This segment cannot be deleted because it is currently referenced within guides and/or other segments.',
            );
        },
        drawerClass () {
            if (this.isInsightsSegment) {
                return 'segment-builder segment-builder--insights-segment';
            }

            return 'segment-builder segment-builder--non-insights-segment';
        },
        showRulesPanel () {
            return this.ready && this.isDrawerOpened;
        },
        isInvolvedWithNesting () {
            return isNestedSegment(this.localSegment) || this.parentSegmentsById(this.localSegment?.id).length > 0;
        },
        visibilityOptions () {
            const disableOnlyMeForNesting = this.isInvolvedWithNesting && !this.isNewSegment;

            return [
                {
                    disabled:
                        !!this.fetchingSegments ||
                        !this.localSegment ||
                        (!this.canShare && this.localSegment.shared === false),
                    label: this.$t('editSegmentOverlay.Everyone'),
                    value: true,
                },
                {
                    disabled:
                        !!this.fetchingSegments ||
                        !this.localSegment ||
                        !!disableOnlyMeForNesting ||
                        (!this.canShare && this.localSegment.shared === true) ||
                        !!this.onlyAllowGuideCompatibleRules,
                    label: this.$t('editSegmentOverlay.Only me'),
                    value: false,
                },
            ];
        },
        visibilityTooltip () {
            if (isNestedSegment(this.localSegment)) {
                return this.$t('editSegmentOverlay.Segments with nested segments must have visibility set to Everyone');
            }
            if (this.isInvolvedWithNesting) {
                return this.$t(
                    'editSegmentOverlay.Segments referenced within a nested segment must have visibility set to Everyone',
                );
            }

            return '';
        },
        insightsVisibilityTooltip () {
            if (this.isInsightsSegment) {
                return this.$t('editSegmentOverlay.Insights are available for all Insights users.');
            }

            return '';
        },
        title () {
            if (this.isCustomSegment) {
                return this.$t('editSegmentOverlay.Custom segment');
            }

            if (this.isBuiltSegment) {
                if (!this.canEdit && !this.canShare && !this.canDelete) {
                    return this.$t('editSegmentOverlay.View segment');
                }

                return this.$t('editSegmentOverlay.Edit segment');
            }

            if (this.isInsightsSegment) {
                return this.$t('editSegmentOverlay.Create insights segment');
            }

            return this.$t('editSegmentOverlay.Create segment');
        },
        cancelButtonText () {
            if (this.isAdoptSegment || !this.hasUnacknowledgedChanges) {
                return this.$t('Close');
            }

            return this.$t('Cancel');
        },
        saveButtonText () {
            if (this.isCustomSegment) {
                return this.$t('editSegmentOverlay.Save custom segment');
            }

            if (this.isBuiltSegment) {
                return this.$t('editSegmentOverlay.Save segment');
            }

            if (this.isInsightsSegment) {
                return this.$t('editSegmentOverlay.Create insights segment');
            }

            if (this.isSmartSegment) {
                return this.$t('editSegmentOverlay.Save and add to journey');
            }

            return this.$t('editSegmentOverlay.Create segment');
        },
        saveButtonTooltip () {
            return this.editingWithoutChanges ? this.$t('editSegmentOverlay.No changes made') : undefined;
        },
        isCustomSegment () {
            return this.localSegment.id === 'custom';
        },
        isBuiltSegment () {
            return !this.isCustomSegment && Boolean(this.localSegment.id);
        },
        isNewSegment () {
            return !this.localSegment.id;
        },
        createdBySomeoneElse () {
            const { createdByUser } = this.localSegment;

            return createdByUser && createdByUser.id !== this.user.id;
        },
        ownedByCurrentUser () {
            return !this.isBuiltSegment || !this.createdBySomeoneElse;
        },
        hasDependentSegment () {
            return Boolean(this.dependentCustomSegments.length || this.dependentDraftSegments.length);
        },
        hasDependentReplayApp () {
            return (
                this.dependentReplayApps.length > 0 ||
                this.dependentCustomSegments.some((dependentSegment) => dependentSegment.type === 'replay')
            );
        },
        isGuideEligible () {
            return this.segmentIsGuideFriendly(this.localSegment);
        },
        showSegmentFlag () {
            if (!this.isSuperUser) return false;
            if (this.createdBySomeoneElse) return false;
            if (this.isInsightsSegment) return false;

            // Legacy sub (in US prod) used internally for Pendo Feedback (instead of pendo-internal).
            if (this.subscription.name === 'Receptive1' && this.subscription.id === '5765439377113088') return true;
            if (this.subscription.name === 'pendo-internal') return true;

            return false;
        },
        showGuidesToAnon () {
            return this.guidesOnly ? this.getApplicationFlag(this.targetAppId, 'showGuidesToAnon') : null;
        },
        showAnonymousOptions () {
            if (this.isReplay) {
                return !this.isInsightsSegment && this.isCustomSegment && this.showAnonymousEnabled;
            }

            return !this.isInsightsSegment && this.isCustomSegment && this.showGuidesToAnon;
        },
        pipeline () {
            const { compound, pipeline, pipelines } = this.localSegment;
            const displayObject = compound ? { compound, pipelines } : { pipeline };

            return displayObject;
        },
        pipelineJson () {
            return JSON.stringify(this.pipeline, null, 2);
        },
        aggBuilderJson () {
            const pipeline = eligibleVisitorPipeline({ segment: this.pipeline, livePreview: true });

            return JSON.stringify(pipeline, null, 2);
        },
        onlyAllowGuideCompatibleRules () {
            return (
                this.guidesOnly ||
                this.dependentGuides.length > 0 ||
                this.dependentCustomSegments.length > 0 ||
                this.hasDependentReplayApp
            );
        },
        segmentFlagNameIsValid () {
            const flagName = get(this.localSegment, 'flagName', '');

            // Regular Expression taken from src/pendo.io/model/segment.go:38
            return flagName.length === 0 || !!flagName.match(/^[$A-Za-z_][0-9A-Za-z_$]*$/g);
        },
        editingWithoutChanges () {
            return this.isBuiltSegment && !this.hasUnacknowledgedChanges;
        },
        saveButtonDisabled () {
            return (
                !this.isValid ||
                !this.isFormValid ||
                !this.showRulesPanel ||
                !this.segmentFlagNameIsValid ||
                this.editingWithoutChanges ||
                this.loadingVerification ||
                this.hasDescriptionError ||
                this.isSegmentEditorReadOnly ||
                this.hasNoRules ||
                this.hasSessionPropertiesInvalidDate ||
                this.segmentIsBelowMinVisitorCount
            );
        },
        verificationAcceptButtonDisabled () {
            return (
                this.isSaving || this.loadingVerification || (this.hasUnacknowledgedChanges && this.saveButtonDisabled)
            );
        },
        isSegmentEditorReadOnly () {
            return this.isInsightsSegment && !this.insightsSelectedApp.id;
        },
        hasNoRules () {
            return this.isInsightsSegment && isEmpty(this.localSegment.definition.filters);
        },
        hasSessionPropertiesInvalidDate () {
            if (this.localSegment?.definition?.filters.length) {
                return this.localSegment.definition.filters.some((filter) => {
                    return this.invalidDate(filter);
                });
            }

            return false;
        },
        segmentIsBelowMinVisitorCount () {
            return this.getFeatureFlag('enableMinVisitorsInSegment') && (this.numVisitorsInSegment.count < this.minVisitorsInSegment || this.numVisitorsInSegment.isLoading);
        },
        insightsSegmentShowEligibleVisitorsCount () {
            return this.getFeatureFlag('enableMinVisitorsInSegment');
        },
        // legacy adopt segment builder data
        filteredSchemaList () {
            const schemaList = reduceSchema(this.schemas.visitor, 'visitor');
            const filteredSchema = schemaList.filter((schema) => {
                switch (schema.id) {
                    case 'resourceCenter':
                        return this.activeHasResourceCenter;
                    case 'page':
                    case 'feature':
                    case 'trackType':
                        return this.$productLine(PRODUCT_LINES.ANALYTICS);
                    case 'workflow':
                        return this.activeHasWorkflows;
                }

                return true;
            });

            return filteredSchema.filter((schema) => {
                switch (schema.id) {
                    case 'guide':
                    case 'guideElement':
                        return !!this.guideList.length;
                    case 'resourceCenter':
                        return !!this.draftResourceCenterList.length;
                    case 'workflow':
                        return !!this.workflowsList.length;
                    case 'feature':
                        return !!this.featureList.length;
                    case 'page':
                        return !!this.pageList.length;
                    case 'trackType':
                        return !!this.trackTypeList.length;
                }

                return true;
            });
        },
        hasAdoptCustomPageSegments () {
            return this.$segmentFlag('adoptCustomPageSegments');
        },
        canShowAnonymousVisitors () {
            return this.getFeatureFlag('showAnonymous');
        },
        adoptUISegmentsMap () {
            return keyBy(this.listAdoptUISegments, 'id');
        },
        segmentsListByGroup () {
            return segmentsList(this.adoptUISegmentsMap, this.canShowAnonymousVisitors);
        },
        activeHasWorkflows () {
            return this.$productLine(PRODUCT_LINES.ANALYTICS, 'workflowEnabled');
        },
        activeHasResourceCenter () {
            return this.$productLine(PRODUCT_LINES.GUIDES, 'maxPublicResourceCentersAllowed') > 0;
        },
        editingGuideSegment () {
            // find limitForGuideUsage or use onlyAllowGuideCompatibleRules???
            return this.guidesOnly || isSegmentAttachedToGuide(this.editingSegment, this.guideMap);
        },
        rcModulesMap () {
            return keyBy(this.rcModulesList, 'id');
        },
        appFirstVisit () {
            const firstVisits = this.listAllForActiveSubscription.map((app) => app.firstVisit).filter(Boolean);

            const minValue = firstVisits.length ? min(firstVisits) : 0;
            const today = new Date();
            const defaultFirstVisit = today.setFullYear(today.getFullYear() - 1);

            return minValue || defaultFirstVisit;
        },
        workflowWithinLastDateRange () {
            const { spec, config } = this.activeTimeSeries;

            return spec.count >= 7 && config.type !== 'Custom' ? spec.count : 0;
        },
    },
    watch: {
        isInvolvedWithNesting: {
            immediate: true,
            handler () {
                this.updateVisibilityIfNested();
            },
        },
    },
    async created () {
        if (this.isInsightsSegment) {
            this.isLoadingApps = true;
            await this.loadAllApps();
            this.isLoadingApps = false;
        }

        if (this.activeHasResourceCenter) {
            await this.loadAllResourceCenters();
        }
    },
    beforeUnmount () {
        this.cancelEditSegmentAndEmit();
    },
    methods: {
        ...mapMutations('segments', ['cancelEditSegment']),
        ...mapActions({
            saveSegment: 'segments/saveSegment',
            loadDependents: 'segments/loadDependents',
            loadAllApps: 'apps/loadAll',
            updateVerificationState: 'segments/updateSegmentVerificationState',
            loadAllResourceCenters: 'guides/resourceCenter/loadAll',
            createSegment: 'segments/createSegment',
        }),
        async onDrawerOpen () {
            this.showSettingsPanel = true;
            await this.initLocalSegment();
            this.updateVisibilityIfNested();

            // Default to only me for any non existing segment, unless creating for a guide or Ask Pendo
            if (!this.guidesOnly && !this.isBuiltSegment && !this.isInsightsSegment && !this.isAskPendoSegment) {
                this.localSegment.shared = false;
            }
        },
        onDrawerOpened () {
            this.isDrawerOpened = true;
        },
        updateVisibilityIfNested () {
            if (!this.isNewSegment && this.isInvolvedWithNesting && !this.localSegment.shared) {
                this.updateVisibility(true);
            }
        },
        resetLocalState () {
            this.showConfirmExitModal = false;
            this.showSettingsPanel = false;
            this.isDrawerOpened = false;
            this.localSegment = {};
            this.isSaving = false;
            this.ready = false;
            this.hasUnacknowledgedChanges = false;
            this.isValid = true;
            this.isFormValid = true;
            this.dependentGuides = [];
            this.dependentReports = [];
            this.dependentSegments = [];
            this.dependentCustomSegments = [];
            this.dependentDraftSegments = [];
            this.dependentReplayApps = [];
            if (this.$route.query.segment === 'createNew') {
                const { segment, location, ...query } = this.$route.query;
                this.$router.replace({ query });
            }
        },
        onDeleteSegment (deletedSegments) {
            this.showDeleteSegmentModal = false;
            this.cancelEditSegment();
            this.$emit('delete', deletedSegments);
        },
        onSave () {
            if (!this.segmentFlagNameIsValid) {
                return;
            }

            if (!this.isValid) {
                this.checkValidRule = true;

                return;
            }

            if (!!this.isInvolvedWithNesting && !this.localSegment.shared) {
                this.showChangeVisibilityModal = true;

                return;
            }

            if (this.hasDependentReplayApp) {
                this.showReplayConfirmSaveModal = true;

                return;
            }

            if (this.confirmRequestAction === 'verify') {
                this.showConfirmRequestActionModal = true;

                return;
            }

            if (this.verificationState !== 'unverified') {
                this.showConfirmResetRequestModal = true;

                return;
            }

            // element-ui's number input (which we wrap in our component library) does not emit an input
            // event when the user types into the input, it only emits a change event on blur.
            // waiting here before save ensures we have the latest up to date value if the user clicks
            // save while the number input is focused.

            // can be removed if https://pendo-io.atlassian.net/browse/APP-31744 is resolved
            setTimeout(() => this.save(), 200);
        },
        async save () {
            this.isSaving = true;
            try {
                if (this.localSegment.verification) {
                    this.localSegment.verification.state =
                        this.confirmRequestAction === 'verify' ? 'Verified' : 'Unverified';
                }
                const params = {
                    segment: this.localSegment,
                    ...(this.insightsSelectedApp.id && { insightsAppId: this.insightsSelectedApp.id }),
                    location: this.location,
                };
                const segment = await this.saveSegment(params);

                this.hasUnacknowledgedChanges = false;
                this.confirmRequestAction = '';

                if (this.isInsightsSegment) {
                    PendoNotification({
                        type: 'success',
                        message: this.$t(
                            'editSegmentOverlay.Insight segment created successfully.\n If your segment meets insight eligibility requirements, you can expect to see insights in the next 25 hours.',
                        ),
                    });
                }
                this.$emit('save', segment);
            } catch {
                PendoNotification({
                    type: 'error',
                    title: this.$t('editSegmentOverlay.Segment was not saved'),
                    message: this.$t(
                        'editSegmentOverlay.Looks like something went wrong when saving your segment. Please refresh and try again.',
                    ),
                    duration: 3000,
                });
            } finally {
                this.isSaving = false;
            }
        },
        openDeleteSegmentModal () {
            const segmentsToDelete = [this.editor.segment];

            this.loadDependents({ segments: segmentsToDelete });
            this.segmentsToDelete = segmentsToDelete;
            this.showDeleteSegmentModal = true;
        },
        closeDeleteSegmentModal () {
            this.segmentsToDelete = [];
            this.showDeleteSegmentModal = false;
        },
        onSegmentEditorUpdate (...args) {
            const keysToOmit = ['key', '$valid'];

            if (!this.hasUnacknowledgedChanges) {
                if (args[0].length !== this.localSegment.definition.filters.length) {
                    this.hasUnacknowledgedChanges = true;
                } else {
                    const oldFilters = this.localSegment.definition.filters.map((filter) => omit(filter, keysToOmit));
                    const newFilters = args[0].map((filter) => omit(filter, keysToOmit));
                    this.hasUnacknowledgedChanges = !isEqual(oldFilters, newFilters);
                }
            }

            this.updateSegmentFilters(...args);
        },
        onDescriptionInput () {
            if (this.isDrawerOpened) {
                this.hasUnacknowledgedChanges = true;
            }
            this.autoResizeDescriptionTextarea();
        },
        autoResizeDescriptionTextarea () {
            this.$nextTick(() => {
                const textarea = this.$refs.descriptionTextarea?.$el?.querySelector('textarea');
                if (textarea) {
                    textarea.style.height = 'auto';
                    textarea.style.height = `${textarea.scrollHeight}px`;
                }
            });
        },
        updateVisibility (shared) {
            this.hasUnacknowledgedChanges = true;
            this.$set(this.localSegment, 'shared', shared);
        },
        updateSegmentFlag (flagName) {
            this.hasUnacknowledgedChanges = true;
            this.$set(this.localSegment, 'flagName', flagName);
        },
        updateName (name) {
            this.hasUnacknowledgedChanges = true;
            name = removeSpecialChars(name);
            this.$set(this.localSegment, 'name', name);
        },
        onAnonVisitorInput (...args) {
            this.hasUnacknowledgedChanges ||= !isEqual(args[0], this.localSegment.definition.advancedOptions);
            this.updateSegmentAdvancedOptions(...args);
        },
        cancelEditSegmentAndEmit () {
            // We always need to do both of these actions at once.
            // Some callers of this overlay use Vuex to manage the `visible` prop,
            // while others update `visible` directly using events like @close.
            this.cancelEditSegment();
            this.$emit('close');
        },
        onDrawerBeforeClose (evt) {
            // When the drawer is closed by a parent setting `visible` to false,
            // we should not interfere.
            if (this.visible) {
                if (this.hasUnacknowledgedChanges) {
                    evt.stop();
                    this.showConfirmExitModal = true;
                } else {
                    // In case the drawer closing is triggered internal to this component,
                    // as opposed to being triggered by a parent setting visible to false,
                    // we need to emit the close event ourselves to keep props in sync.
                    this.cancelEditSegmentAndEmit();
                }
            }
        },
        onDrawerCancel () {
            if (this.hasUnacknowledgedChanges) {
                this.showConfirmExitModal = true;
            } else {
                this.cancelEditSegmentAndEmit();
            }
        },
        onDismissExitModal () {
            this.showConfirmExitModal = false;
        },
        onConfirmExitModal () {
            this.showConfirmExitModal = false;
            this.hasUnacknowledgedChanges = false;
            this.cancelEditSegmentAndEmit();
        },
        onDismissChangeVisibilityModal () {
            this.showChangeVisibilityModal = false;
        },
        onSaveChangeVisibilityModal () {
            this.showChangeVisibilityModal = false;
            this.updateVisibility(true);
            this.onSave();
        },
        async onSaveReplayConfirmModal () {
            await this.save();
            this.showReplayConfirmSaveModal = false;
        },
        onOpenAggBuilder () {
            window.open(
                this.$router.pendo.toPath({
                    name: 'AggregationsBuilder',
                    query: {
                        aggJson: this.aggBuilderJson,
                    },
                }),
            );
        },
        async handleVerificationStatusChange (newStatus, notes) {
            this.loadingVerification = true;
            try {
                await this.updateVerificationState({
                    id: this.localSegment.id,
                    status: capitalize(newStatus),
                    ...(notes && { notes }),
                });
            } catch (err) {
                PendoNotification({
                    type: 'error',
                    duration: 4000,
                    title: this.$t('editSegmentOverlay.Error updating verification status'),
                    message:
                        err.response?.data ||
                        this.$t('editSegmentOverlay.An error occurred while updating the verification status'),
                });
            } finally {
                this.loadingVerification = false;
            }
        },
        async onConfirmResetRequest () {
            this.showConfirmResetRequestModal = false;
            if (this.keepVerifiedStatus) {
                this.confirmRequestAction = 'verify';
                this.keepVerifiedStatus = false;
            }
            await this.save();
        },
        onAcceptVerification () {
            this.confirmRequestAction = 'verify';

            if (this.hasUnacknowledgedChanges) {
                // Check for general segment validity;
                // display one or more confirmation modals if necessary
                this.onSave();
            } else {
                this.showConfirmRequestActionModal = true;
            }
        },
        onRejectVerification () {
            this.confirmRequestAction = 'reject';
            this.showConfirmRequestActionModal = true;
        },
        async onConfirmRequestAction () {
            this.showConfirmRequestActionModal = false;
            const newStatus = ACTION_STATE_MAP[this.confirmRequestAction] || this.confirmRequestAction;

            if (newStatus === 'verified' && this.hasUnacknowledgedChanges) {
                // Save all segment changes at once, including verification change
                await this.save();
            } else {
                await this.handleVerificationStatusChange(newStatus);
            }
        },
        onDismissRequestAction () {
            this.showConfirmRequestActionModal = false;
        },
        invalidDate (filter) {
            if (filter.id === 'utm' || filter.filterType === 'utm') {
                if (filter.time === 'between' && moment(filter.first, { ignoreTimeZone: true }).valueOf() < UTM_DATE) {
                    return true;
                }
                if (filter.time === 'withinLast') {
                    const today = moment({ ignoreTimeZone: true }).startOf('day');
                    const shifted = today.clone().subtract(filter.count, filter.granularity);
                    const shiftedMs = shifted.valueOf();
                    if (shiftedMs < UTM_DATE) {
                        return true;
                    }
                }
            }

            return false;
        },
        onEligibleVisitorsUpdated (eligibleVisitors) {
            this.numVisitorsInSegment = {
                count: eligibleVisitors.count,
                isLoading: eligibleVisitors.isLoading,
            };
        },
        onCreateNewSegment () {
            this.createSegment();
        },
        async onLegacyAdoptSegmentSave ({ segment }) {
            const isValid = isValidSegmentInContext(segment, {
                guideMap: this.guideMap,
                assigningToGuide: this.editingGuideSegment,
            });
            if (!isValid) return;

            this.isSaving = true;

            let finalSegment;

            try {
                finalSegment = await this.saveSegment({ segment });
            } catch {
                PendoNotification({
                    type: 'error',
                    title: this.$t('editSegmentOverlay.Segment was not saved'),
                    message: this.$t(
                        'editSegmentOverlay.Looks like something went wrong when saving your segment. Please refresh and try again.',
                    ),
                    duration: 3000,
                });
                finalSegment = this.adoptUISegmentsMap[segment.id];
            } finally {
                this.isSaving = false;
            }
            this.$emit('save', finalSegment);
            this.$emit('close');
        },
    },
};
</script>

<style scoped lang="less">
.super-only {
    color: var(--palette-gray-60);
}
</style>

<!-- eslint-disable-next-line vue-scoped-css/enforce-style-type -->
<style lang="less">
.segment-builder {
    /* styles specific to insights-segment */
    &.segment-builder--insights-segment {
        .pendo-drawer__body {
            grid-template-columns: minmax(275px, 350px) minmax(610px, 1fr);
        }
        .rules-panel {
            padding-right: 32px !important;
        }
        .segment-editor {
            max-width: 1140px;
        }

        @media (max-width: 916px) {
            .pendo-drawer__body {
                padding: 0;
                grid-row-gap: 24px;
                grid-template-columns: 1fr;
                grid-template-rows: max-content;
                overflow-y: auto;
            }
            .settings-panel__inner {
                position: unset !important;
            }
            .settings-panel {
                padding-top: 48px;
                padding-bottom: 0px;
            }

            .rules-panel {
                padding-top: 0px;
                padding-bottom: 48px;
            }

            .rules-panel,
            .settings-panel {
                padding-left: 32px;
                padding-right: 32px;
                overflow-y: initial;
            }
        }
    }

    /* styles specific to all other segments (other than insights) */
    &.segment-builder--non-insights-segment {
        .pendo-drawer__body {
            grid-template-columns: minmax(275px, 350px) minmax(610px, 1fr) 385px;
        }

        @media (max-width: 1300px) {
            .pendo-drawer__body {
                padding: 0;
                grid-row-gap: 24px;
                grid-template-columns: 1fr;
                grid-template-rows: max-content;
                overflow-y: auto;
            }
            .settings-panel__inner {
                position: unset !important;
            }

            .settings-panel {
                padding-top: 48px;
                padding-bottom: 0px;
            }

            .rules-panel {
                padding-top: 0px;
                padding-bottom: 0;
            }

            .rules-panel,
            .settings-panel,
            .metrics-panel {
                padding-left: 32px;
                padding-right: 32px;
                overflow-y: initial;
            }
            .metrics-panel {
                max-height: 600px;
                padding-top: 0;
            }
        }
    }

    .create-smart-segment-disclaimer {
        padding-bottom: 13px;
    }

    /* styles that apply to both layouts/all segments */
    .pendo-drawer__body {
        display: grid;
        padding: 0;
        grid-column-gap: 24px;
        overflow: hidden;
    }

    .settings-panel,
    .rules-panel,
    .metrics-panel {
        padding: 20px 0;

        .segment-editor {
            margin-right: auto;
        }
    }

    .metrics-panel {
        padding-right: 32px;
        overflow-y: auto;
    }

    .settings-panel {
        padding-left: 32px;
        position: relative;

        .settings-panel__inner {
            position: absolute;
            top: 20px;
            bottom: 12px;
            left: 32px;
            right: 0;
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .verification-card {
            line-height: 1.5;

            .request-metadata:last-child {
                margin-bottom: 0;
            }

            .pendo-card__footer {
                display: flex;
                justify-content: flex-end;
                padding: 8px;
            }
        }

        .settings-card {
            width: 100%;
            overflow: hidden;
            max-height: 100%;
            flex: 1;
            display: flex;
            flex-direction: column;
            height: fit-content;

            .pendo-card__body {
                padding: 16px 0;
                height: 100%;
                overflow-y: auto;
            }
        }

        .settings-panel__section {
            padding: 0 16px;
        }

        .created-by {
            margin-top: 16px;
        }

        .created-by-details {
            display: flex;
            flex-direction: row;
            align-items: center;

            .pendo-icon {
                margin-right: 8px;
            }
        }

        &__label {
            display: grid;
            height: 24px;
            font-weight: 600;
            align-items: start;
        }

        .name-input .pendo-form-item {
            margin-bottom: 0;
        }

        .name-input,
        .flag-input,
        .visibility-options {
            max-width: 320px;
        }

        .created-by-label,
        .conditions-label,
        .segment-flag-label {
            display: grid;
            grid-auto-flow: column;
            grid-gap: 8px;
            justify-content: start;
            font-weight: 600;
            height: 24px;
        }

        .conditions-pipeline {
            position: relative;

            .pendo-button {
                position: absolute;
                top: 2px;
                right: 8px;
            }

            .pendo-button + .pendo-button {
                position: absolute;
                top: 2px;
                right: 28px;
            }
        }
    }

    .rules-panel {
        overflow-y: auto;
    }

    .delete-segment-modal__footer,
    .segment-builder__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        .cancel-segment {
            margin-left: auto;
        }
    }

    .app-chooser-container {
        display: flex;
        flex-direction: column;
        gap: 25px;
        margin-bottom: 20px;
    }

    .segments-used {
        padding: 0 16px;

        .segments-used-count {
            font-weight: 600;
            font-size: 22px;
            line-height: 22px;
            margin-right: 8px;
            padding: 16px;
        }
        .segments-used-label {
            display: grid;
            grid-auto-flow: column;
            grid-gap: 8px;
            justify-content: start;
            font-weight: 600;
            height: 24px;
        }
    }

    .description-input {
        .pendo-input__field {
            padding: 0 !important;
            min-height: unset !important;
        }

        .pendo-input__textarea {
            min-height: 20px;
            padding: 8px !important;
            width: 100% !important;
            resize: none;
            overflow: hidden;
        }

        .pendo-input__label--bottom {
            text-align: right;
            margin-top: 4px;
        }

        .description-input__character-count {
            font-size: 12px;
            color: var(--palette-gray-80);

            &--error {
                color: var(--palette-red-80);
            }
        }
    }
}
</style>
